/** Scope-locked client contract for ORDER 3b's four expiry-tide cells. */
import type { VenueState } from "./api";

export type ExpiryTide = "index" | "etf";
export type ExpirationScope = "zero_dte" | "weekly";
/** TIDE-DOOR-VERDICT-1: the door reads STALE past its budget where it used to read OK. OK + stale:true is still
 *  admitted — the worker served before chain 129 says exactly that — so a ship that carries either half parses. */
export type TideState = "OK" | "EMPTY" | "STALE";

export interface ExpiryTideScope {
  tide: ExpiryTide;
  expiration: ExpirationScope;
}

export interface ExpiryTideBar {
  root: "INDEX" | "ETF";
  t: number;
  timestamp: string;
  market_date: string;
  /** A9 (r5.1): null only on an INDEX body (price_symbol SPX) whose bar declares spot_state ABSENT. */
  underlying_price: number | null;
  net_call_premium: number;
  net_put_premium: number;
  net_volume: number;
  vendor_ts: string;
  receipt_ts: string;
  poll_seq: string;
}

export interface ExpiryTideCell {
  root: "INDEX" | "ETF";
  tide: ExpiryTide;
  expiration: ExpirationScope;
  state: TideState;
  bars: ExpiryTideBar[];
  ageS: number | null;
  budgetS: number;
  stale: boolean | null;
  vendorTs: string | null;
  receiptTs: string | null;
  pollSeq: string | null;
  /** ORDER 114: the Worker calendar verdict travels with the cell; the browser does not infer it. */
  venueState?: VenueState;
}

/**
 * ⚠ `ExpiryTideCrossDirection`, `ExpiryTideCrossover` AND `expiryTideCrossovers` ARE RETIRED —
 * ORDER 42 / DESK-n38 R1103, owner word 2026-08-31: the purple crossover diamonds and their
 * CROSSOVERS chip come off the DTE tide panels entirely. They were never calibrated (the tide
 * STUDY has still not ruled), so nothing downstream may re-derive them under another name.
 * ⛔ THEY ARE GONE FROM THIS MODULE, not merely unrendered. A helper that still computes marks
 * nothing draws is an invitation to draw them again; the retirement is DECLARED here and NAMED
 * in the panels' `data-retired-series-ids` census (`expiryTideCross:diamond` /
 * `expiryTideCross:stamp` / `expiryTideCross:chip`), the `parseStoredLayout` idiom — a loss that
 * is stated is a decision, a loss that is silent is a regression.
 * The tide lines, the spot line, the volume bars and the staleness chips are UNAFFECTED.
 */

export interface ExpiryTideReadback {
  t: number;
  label: string;
  callPremium: number;
  putPremium: number;
  spot: number | null;
  volume: number;
}

type RecordValue = Record<string, unknown>;

const isRecord = (value: unknown): value is RecordValue =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const expectedRoot = (tide: ExpiryTide): "INDEX" | "ETF" => tide === "index" ? "INDEX" : "ETF";

export function formatExpiryTideMinute(tMs: number): string {
  if (!Number.isFinite(tMs)) throw new TypeError("expiry tide minute timestamp must be finite");
  const date = new Date(tMs);
  return `${String(date.getUTCHours()).padStart(2, "0")}:${String(date.getUTCMinutes()).padStart(2, "0")}Z`;
}

/** Point-in-time synchronized readback: the last complete bar at or before the shared cursor. */
export function expiryTideReadbackAt(
  bars: readonly ExpiryTideBar[],
  cursorTMs: number | null,
): ExpiryTideReadback | null {
  if (cursorTMs === null || bars.length === 0) return null;
  if (!Number.isFinite(cursorTMs)) throw new TypeError("expiry tide cursor timestamp must be finite");
  let found: ExpiryTideBar | null = null;
  for (const bar of bars) {
    if (bar.t > cursorTMs) break;
    const receiptTMs = Date.parse(bar.receipt_ts);
    if (!Number.isFinite(receiptTMs)) throw new TypeError("expiry tide bar receipt_ts is not a timestamp");
    if (receiptTMs <= cursorTMs) found = bar;
  }
  return found ? {
    t: found.t,
    label: formatExpiryTideMinute(found.t),
    callPremium: found.net_call_premium,
    putPremium: found.net_put_premium,
    spot: found.underlying_price,
    volume: found.net_volume,
  } : null;
}

/** A5: hover reads the containing vendor bucket, independently of its receipt clock.
 * null names ABSENT to the caller; replay keeps expiryTideReadbackAt's receipt gate.
 */
export function expiryTideBucketAt(
  bars: readonly ExpiryTideBar[], minuteMs: number | null, bucketMs: number,
): ExpiryTideReadback | null {
  if (minuteMs !== null && !Number.isFinite(minuteMs)) throw new TypeError("expiry tide minute must be finite");
  if (!Number.isFinite(bucketMs) || bucketMs <= 0) throw new TypeError("expiry tide bucket must be positive and finite");
  if (minuteMs === null) return null;
  const found = bars.find((bar) => bar.t <= minuteMs && minuteMs < bar.t + bucketMs);
  return found ? {
    t: found.t, label: formatExpiryTideMinute(found.t),
    callPremium: found.net_call_premium, putPremium: found.net_put_premium,
    spot: found.underlying_price, volume: found.net_volume,
  } : null;
}

/** A10: compare the bars with the served venue session's ET calendar date. */
export function expiryTideBehind(cell: ExpiryTideCell): null | { sessionDate: string; barsDate: string; friday: boolean } {
  if (cell.bars.length === 0) return null;
  const open = cell.venueState?.current_session?.open ?? cell.venueState?.last_session_open;
  if (open === null || open === undefined) return null;
  if (typeof open !== "string" || !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?(?:Z|[+-]\d{2}:\d{2})$/.test(open) || !Number.isFinite(Date.parse(open))) {
    throw new TypeError("expiry tide session open is not a timestamp");
  }
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/New_York", year: "numeric", month: "2-digit", day: "2-digit", weekday: "short",
  }).formatToParts(new Date(open));
  const part = (name: string) => parts.find((item) => item.type === name)!.value;
  const sessionDate = `${part("year")}-${part("month")}-${part("day")}`;
  const barsDate = cell.bars[0].market_date;
  return barsDate < sessionDate ? { sessionDate, barsDate, friday: part("weekday") === "Fri" } : null;
}

/** A9: the spine lags the bar, so the INDEX spot may be served null with spot_state ABSENT; nowhere else. */
function spotOrAbsent(value: Record<string, unknown>, priceSymbol: unknown, root: "INDEX" | "ETF", index: number): number | null {
  if (root === "INDEX" && value.underlying_price === null && priceSymbol === "SPX" && value.spot_state === "ABSENT") return null;
  return finite(value.underlying_price, `expiry tide bar ${index} underlying_price`);
}

function finite(value: unknown, label: string): number {
  if (value === null || value === undefined || (typeof value === "string" && value.trim() === "")
    || (typeof value !== "string" && typeof value !== "number")) {
    throw new TypeError(`${label} is absent or blank`);
  }
  const number = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(number)) throw new TypeError(`${label} is not finite`);
  return number;
}

function timestamp(value: unknown, label: string): string {
  if (typeof value !== "string" || !Number.isFinite(Date.parse(value))) {
    throw new TypeError(`${label} is not a timestamp`);
  }
  return value;
}

function nullableTimestamp(value: unknown, label: string): string | null {
  return value === null ? null : timestamp(value, label);
}

function optionalVenueState(value: unknown): VenueState | undefined {
  if (value === undefined) return undefined;
  if (!isRecord(value) || typeof value.verdict !== "string") {
    throw new TypeError("expiry tide venue_state is not a served venue decision");
  }
  return value as unknown as VenueState;
}

export function expiryTidePath(tide: ExpiryTide, expiration: ExpirationScope): string {
  return `/api/tide/expiry?tide=${encodeURIComponent(tide)}&expiration=${encodeURIComponent(expiration)}`;
}

/** The generic chip handles age > budget; this preserves independent server alarms such as skew. */
export function expiryTideStaleError(meta: {
  stale: boolean | null;
  ageS: number | null;
  budgetS: number;
}): string | null {
  if (meta.stale !== true) return null;
  if (meta.ageS !== null && meta.ageS > meta.budgetS) return null;
  return meta.ageS !== null && meta.ageS < 0
    ? "server marked tide stale — vendor clock skew"
    : "server marked tide stale independently of age";
}

/**
 * Bound dense session bars below their one-minute point spacing so later bars cannot obscure them.
 * A zero plot width is the layout-not-ready WAIT sentinel and returns zero bar width; negative or
 * non-finite widths remain wrong-state refusals.
 */
export function expiryTideVolumeWidth(
  count: number,
  plotWidth: number,
  actualMinimumSpacing?: number,
): number {
  if (!Number.isInteger(count) || count <= 0) throw new RangeError("volume bar count must be positive");
  if (plotWidth === 0) return 0;
  if (!Number.isFinite(plotWidth) || plotWidth < 0) throw new RangeError("volume plot width must be positive");
  if (actualMinimumSpacing !== undefined && (!Number.isFinite(actualMinimumSpacing) || actualMinimumSpacing <= 0)) {
    throw new RangeError("actual volume point spacing must be positive when supplied");
  }
  const spacing = actualMinimumSpacing ?? (count < 2 ? plotWidth : plotWidth / (count - 1));
  return Math.min(spacing, Math.max(0.7, Math.min(4, spacing * 0.8)));
}

/**
 * Refuse cross-cell contamination at the UI boundary even if a future route regression returns
 * valid-looking sibling bytes. An EMPTY register is accepted only with zero bars; it never falls
 * back to the other expiration or the other tide family.
 */
export function parseExpiryTideCell(
  payload: unknown,
  expected: ExpiryTideScope,
): ExpiryTideCell {
  if (!isRecord(payload) || payload.ok !== true) {
    throw new TypeError("expiry tide envelope is not an ok object");
  }
  const root = expectedRoot(expected.tide);
  const state = payload.tide_state;
  const identityMatches = payload.root === root
    && payload.tide === expected.tide
    && payload.expiration === expected.expiration;
  if (!identityMatches) {
    throw new TypeError(
      `expiry tide response escaped its cell: expected ${expected.tide}/${expected.expiration}/${root}, `
      + `got ${String(payload.tide)}/${String(payload.expiration)}/${String(payload.root)}`,
    );
  }
  if (state !== "OK" && state !== "EMPTY" && state !== "STALE") {
    throw new TypeError(`expiry tide state is unknown: ${String(state)}`);
  }
  if (!Array.isArray(payload.bars)) throw new TypeError("expiry tide bars is not an array");
  if (state === "EMPTY" && payload.bars.length !== 0) {
    throw new TypeError("expiry tide EMPTY escaped its cell with bars");
  }
  if ((state === "OK" || state === "STALE") && payload.bars.length === 0) {
    throw new TypeError(`expiry tide ${state} carries zero bars`);
  }

  const bars = payload.bars.map((value, index): ExpiryTideBar => {
    if (!isRecord(value)) throw new TypeError(`expiry tide bar ${index} is not an object`);
    if (value.root !== root) {
      throw new TypeError(`expiry tide bar ${index} escaped its cell with root ${String(value.root)}`);
    }
    const iso = timestamp(value.timestamp, `expiry tide bar ${index} timestamp`);
    const t = finite(value.t, `expiry tide bar ${index} t`);
    if (t !== Date.parse(iso)) {
      throw new TypeError(`expiry tide bar ${index} has conflicting t and timestamp`);
    }
    if (typeof value.market_date !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(value.market_date)) {
      throw new TypeError(`expiry tide bar ${index} market_date is invalid`);
    }
    return {
      root,
      t,
      timestamp: iso,
      market_date: value.market_date,
      underlying_price: spotOrAbsent(value, payload.price_symbol, root, index),
      net_call_premium: finite(value.net_call_premium, `expiry tide bar ${index} net_call_premium`),
      net_put_premium: finite(value.net_put_premium, `expiry tide bar ${index} net_put_premium`),
      net_volume: finite(value.net_volume, `expiry tide bar ${index} net_volume`),
      vendor_ts: timestamp(value.vendor_ts, `expiry tide bar ${index} vendor_ts`),
      receipt_ts: timestamp(value.receipt_ts, `expiry tide bar ${index} receipt_ts`),
      poll_seq: String(value.poll_seq),
    };
  });

  for (let index = 1; index < bars.length; index += 1) {
    if (bars[index].t <= bars[index - 1].t) {
      throw new TypeError(`expiry tide bars are not strictly time-ascending at ${index}`);
    }
    if (bars[index].market_date !== bars[0].market_date) {
      throw new TypeError("expiry tide response crosses session dates");
    }
  }

  const ageS = payload.age_s === null ? null : finite(payload.age_s, "expiry tide age_s");
  const budgetS = finite(payload.budget_s, "expiry tide budget_s");
  if (budgetS <= 0) throw new TypeError("expiry tide budget_s must be positive");
  if (state === "EMPTY" && (
    ageS !== null || payload.stale !== null || payload.vendor_ts !== null
    || payload.receipt_ts !== null || payload.poll_seq !== null
  )) {
    throw new TypeError("expiry tide EMPTY carries false freshness evidence");
  }
  if ((state === "OK" || state === "STALE") && (ageS === null || typeof payload.stale !== "boolean")) {
    throw new TypeError(`expiry tide ${state} is missing freshness evidence`);
  }
  if (state === "STALE" && payload.stale !== true) {
    throw new TypeError("expiry tide STALE without stale evidence");
  }

  const cell: ExpiryTideCell = {
    root,
    tide: expected.tide,
    expiration: expected.expiration,
    state,
    bars,
    ageS,
    budgetS,
    stale: payload.stale as boolean | null,
    vendorTs: nullableTimestamp(payload.vendor_ts, "expiry tide vendor_ts"),
    receiptTs: nullableTimestamp(payload.receipt_ts, "expiry tide receipt_ts"),
    pollSeq: payload.poll_seq === null ? null : String(payload.poll_seq),
    venueState: optionalVenueState(payload.venue_state),
  };
  // A10, review r1 P2: a malformed session open refuses HERE, inside load()'s catch, so the cell draws its
  // TIDE ERROR; judged later in the panel's reactive derivation it would throw past the error boundary.
  expiryTideBehind(cell);
  return cell;
}
